// src/components/DialogDescargarApp.js
//
// Popup para descargar la app HolaVeci. Detecta la plataforma:
//   - Desktop → QR grande para escanear con el celular
//   - Móvil   → botón directo que abre la tienda de apps
//
// URLs placeholder por ahora — reemplazar cuando la app esté publicada.
// El QR se genera con la API pública de qrserver.com (sin dependencias).
//
// Uso:
//   <DialogDescargarApp
//     abierto={boolean}
//     onCerrar={fn}
//     producto="negocio" | "veci"
//   />

import React from 'react';
import {
  Dialog, DialogContent, IconButton, Box, Typography, Stack, Button, Divider,
} from '@mui/material';
import CloseIcon           from '@mui/icons-material/Close';
import AndroidIcon         from '@mui/icons-material/Android';
import AppleIcon           from '@mui/icons-material/Apple';
import QrCodeScannerIcon   from '@mui/icons-material/QrCodeScanner';
import StorefrontIcon      from '@mui/icons-material/Storefront';
import ShoppingBasketIcon  from '@mui/icons-material/ShoppingBasket';

// ── PLACEHOLDER URLs — reemplazar cuando la app esté publicada ───────
// Recomendación: usar una URL corta bajo dominio propio (holaveci.co/app)
// que redirija a Play Store o App Store según user-agent. Así el QR
// impreso o compartido sigue funcionando aunque cambien las URLs de
// tiendas.
const URL_APP = 'https://holaveci.co/app';
const URL_PLAY_STORE = 'https://play.google.com/store/apps/details?id=co.redeprog.holaveci_app.prod';
const URL_APP_STORE  = 'https://apps.apple.com/co/app/holaveci';   // placeholder

const CONFIG = {
  negocio: {
    icono: StorefrontIcon,
    color: 'primary.main',
    label: 'NEGOCIO',
    tituloLabelColor: '#fff',
    titulo: 'Instala HolaVeci para registrar tu negocio',
    subtitulo: 'Descarga la app, crea tu negocio y empieza a recibir pedidos organizados desde el celular.',
  },
  veci: {
    icono: ShoppingBasketIcon,
    color: 'warning.main',
    label: 'VECI',
    tituloLabelColor: '#fff',
    titulo: 'Instala HolaVeci y pídele al negocio del barrio',
    subtitulo: 'Descarga la app gratis y arma tu pedido a las tiendas de tu sector. También recomienda las que quieres ver aquí.',
  },
};

// Detección de plataforma via user-agent
const esMovil = () => {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
};
const esIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

// Genera URL del QR con colores de marca
const urlQR = (url) => {
  const encoded = encodeURIComponent(url);
  // color verde primary.main (#0F4C3A) sin #, background crema (#FBF7F0)
  return `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encoded}&color=0F4C3A&bgcolor=FBF7F0&margin=8&ecc=M`;
};

const DialogDescargarApp = ({ abierto, onCerrar, producto = 'negocio' }) => {
  const cfg = CONFIG[producto] || CONFIG.negocio;
  const Icono = cfg.icono;
  const movil = esMovil();

  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogContent sx={{ p: 0 }}>
        {/* Cabecera con color del producto */}
        <Box sx={{
          bgcolor: cfg.color, color: '#fff',
          px: 3, py: 2.5, position: 'relative',
        }}>
          <IconButton onClick={onCerrar}
                      sx={{ position: 'absolute', top: 8, right: 8, color: '#fff' }}>
            <CloseIcon />
          </IconButton>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{
              width: 48, height: 48, borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <Icono sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1.5 }}>
                HolaVeci · <b>{cfg.label}</b>
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'Fraunces', lineHeight: 1.2, mt: 0.25 }}>
                {cfg.titulo}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Cuerpo */}
        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Typography sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
            {cfg.subtitulo}
          </Typography>

          {movil ? (
            // ─── Vista móvil: botones directos a la tienda ───
            <Stack spacing={2}>
              <Button
                fullWidth size="large" variant="contained"
                startIcon={esIOS() ? <AppleIcon /> : <AndroidIcon />}
                href={esIOS() ? URL_APP_STORE : URL_PLAY_STORE}
                target="_blank" rel="noopener noreferrer"
                sx={{ bgcolor: cfg.color, py: 1.5, fontSize: 16,
                      '&:hover': { bgcolor: cfg.color, filter: 'brightness(0.92)' } }}
              >
                Descargar app
              </Button>
              <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                Se abre {esIOS() ? 'App Store' : 'Google Play'} en tu celular
              </Typography>
            </Stack>
          ) : (
            // ─── Vista desktop: QR grande ───
            <>
              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} mb={2}>
                <QrCodeScannerIcon color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  Escanea con la cámara de tu celular
                </Typography>
              </Stack>

              <Box sx={{
                display: 'grid', placeItems: 'center',
                p: 2, bgcolor: '#FBF7F0', borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
              }}>
                <Box component="img" src={urlQR(URL_APP)}
                     alt="Código QR para descargar HolaVeci"
                     sx={{ width: 240, height: 240, display: 'block' }} />
              </Box>

              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  también disponible en
                </Typography>
              </Divider>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="outlined" size="small" startIcon={<AndroidIcon />}
                        href={URL_PLAY_STORE} target="_blank" rel="noopener noreferrer">
                  Google Play
                </Button>
                <Button variant="outlined" size="small" startIcon={<AppleIcon />}
                        href={URL_APP_STORE} target="_blank" rel="noopener noreferrer">
                  App Store
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DialogDescargarApp;
